import { ArrowUpRight, Box, LineChart, Presentation, Scale } from "lucide-react";
import { agentMap, sectionStatusMeta, toneSurface } from "@/lib/mockData";
import DocRow from "./DocRow";
import { cn } from "@/lib/utils";

const SECTION_ICONS = {
  Presentation,
  LineChart,
  Scale,
  Box,
};

const VISIBLE = 4;

export default function SectionCard({
  section,
  busyDocIds,
  onAddFile,
  onDraftWithAi,
  onDownload,
  onDelete,
}) {
  const Icon = SECTION_ICONS[section.icon] ?? Box;
  const statusMeta = sectionStatusMeta[section.status] ?? sectionStatusMeta.incomplete;
  const total = section.docs.length;
  const complete = section.docs.filter((d) => d.status === "complete").length;
  const updatedAgent = agentMap[section.updatedBy];
  const tone = toneSurface[updatedAgent?.tone] ?? toneSurface.neutral;
  const isDarkAvatar = updatedAgent?.tone === "dark";

  const visibleDocs = section.docs.slice(0, VISIBLE);
  const hasMore = section.docs.length > VISIBLE;
  const extraCount = section.docs.length - VISIBLE;

  return (
    <section
      data-testid={`section-card-${section.id}`}
      className="card-soft flex flex-col gap-4 rounded-[28px] p-6"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-100 text-zinc-700">
            <Icon className="h-5 w-5" strokeWidth={2.2} />
          </div>
          <div>
            <h2 className="text-[18px] font-extrabold tracking-tight text-zinc-900">
              {section.name}
            </h2>
            <div
              data-testid={`section-count-${section.id}`}
              className="mt-0.5 text-[11.5px] text-zinc-500"
            >
              <span className="font-semibold text-zinc-700">
                {complete} of {total}
              </span>{" "}
              docs complete
            </div>
          </div>
        </div>
        <span
          data-testid={`section-status-${section.id}`}
          className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase",
            statusMeta.chip,
          )}
          style={{ letterSpacing: "0.14em" }}
        >
          {statusMeta.label}
        </span>
      </div>

      {/* Stat row */}
      <div className="flex items-center gap-2 text-[11.5px] text-zinc-500">
        <span>Last updated {section.lastUpdated}</span>
        {updatedAgent && (
          <>
            <span className="text-zinc-300">·</span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className={cn(
                  "flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-extrabold",
                  isDarkAvatar
                    ? "bg-[#0B0B0E] text-white"
                    : cn(tone.bg, "text-zinc-900"),
                )}
                style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}
              >
                {updatedAgent.name[0]}
              </span>
              <span className="font-semibold text-zinc-700">{updatedAgent.name}</span>
            </span>
          </>
        )}
      </div>

      {/* Doc list */}
      <ul className="flex flex-col gap-1">
        {visibleDocs.map((d) => (
          <DocRow
            key={d.id}
            doc={d}
            busy={busyDocIds?.has?.(d.id)}
            onAddFile={onAddFile}
            onDraftWithAi={onDraftWithAi}
            onDownload={onDownload}
            onDelete={onDelete}
          />
        ))}
      </ul>

      {hasMore && (
        <details className="group">
          <summary
            data-testid={`section-view-all-${section.id}`}
            className="cursor-pointer list-none text-[12px] font-semibold text-zinc-600 hover:text-black"
          >
            View all ({extraCount} more)
          </summary>
          <ul className="mt-1 flex flex-col gap-1">
            {section.docs.slice(VISIBLE).map((d) => (
              <DocRow
                key={d.id}
                doc={d}
                busy={busyDocIds?.has?.(d.id)}
                onAddFile={onAddFile}
                onDraftWithAi={onDraftWithAi}
                onDownload={onDownload}
                onDelete={onDelete}
              />
            ))}
          </ul>
        </details>
      )}

      {/* Footer */}
      <div className="mt-auto pt-2">
        <button
          type="button"
          data-testid={`section-manage-${section.id}`}
          className="inline-flex items-center gap-1 text-[12px] font-semibold text-zinc-600 hover:text-black"
        >
          Manage section
          <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.4} />
        </button>
      </div>
    </section>
  );
}
